import { MigrationType } from "./Statistics/MigrationType";

/** Whether a flagged path is still outstanding. Mirrors Domain.Enums.FlaggedPathStatus. */
export enum FlaggedPathStatus {
  Live = "live",
  Cleared = "cleared",
}

export const FlaggedPathStatusLabels: Record<FlaggedPathStatus, string> = {
  [FlaggedPathStatus.Live]: "Live",
  [FlaggedPathStatus.Cleared]: "Cleared",
};

/** Why the client declined the path. Mirrors Domain.Enums.FlaggedPathReason. */
export enum FlaggedPathReason {
  Unknown = "unknown",
  PathFileLimit = "pathFileLimit",
}

export const FlaggedPathReasonLabels: Record<FlaggedPathReason, string> = {
  [FlaggedPathReason.Unknown]: "Unspecified",
  [FlaggedPathReason.PathFileLimit]: "Over the file limit",
};

/**
 * One row of the list: a folder and how much is hanging off it. Counts rather than the occurrences
 * themselves — a root folder every case reaches carries tens of thousands, and they are fetched per path when
 * a row is expanded.
 */
export interface FlaggedPathSummary {
  id: string;
  path: string;
  allowDiscoverySystemWide: boolean;
  allowCasesDiscovery: boolean;
  allowIntakesDiscovery: boolean;
  allowProvidersDiscovery: boolean;
  allowFirmDocumentsFoldersDiscovery: boolean;
  allowTemplatesLibraryDiscovery: boolean;
  liveOccurrenceCount: number;
  clearedOccurrenceCount: number;
  /** Distinct entities still reporting the path — its blast radius. */
  liveEntityCount: number;
  /** Live occurrences a sweep had previously cleared: the folder came back. */
  recurredOccurrenceCount: number;
  /** Most recent report across the live occurrences; absent once nothing reports the path. */
  lastSeenOn?: string;
  status: FlaggedPathStatus;
  createdBy: string;
  modifiedBy?: string;
  dateCreated: string;
  dateModified?: string;
}

/**
 * One source record that led discovery to the path. Nullable fields are optional rather than `| null`: the API
 * serializes with JsonIgnoreCondition.WhenWritingNull, so an absent value is an absent property.
 */
export interface FlaggedPathOccurrence {
  id: string;
  entityId?: string;
  migrationType: MigrationType;
  migrationEntityId: string;
  allowDiscovery: boolean;
  sourceTable?: string;
  sourceRecordColumn?: string;
  sourceRecordId?: string;
  sourceTabLabel?: string;
  reason?: FlaggedPathReason;
  reasonDetail?: string;
  lastSeenOn: string;
  /** Set once the path stopped being reported for this record. Always inferred from absence, never observed. */
  clearedOn?: string;
  reopenedOn?: string;
  isLive: boolean;
}

/** A live occurrence that had previously been cleared. Mirrors FlaggedPathOccurrence.HasRecurred. */
export const hasRecurred = (occurrence: FlaggedPathOccurrence): boolean =>
  occurrence.isLive && !!occurrence.reopenedOn;

export interface UpdateFlaggedPathRequest {
  path: string;
  allowDiscoverySystemWide: boolean;
  allowCasesDiscovery: boolean;
  allowIntakesDiscovery: boolean;
  allowProvidersDiscovery: boolean;
  allowFirmDocumentsFoldersDiscovery: boolean;
  allowTemplatesLibraryDiscovery: boolean;
  occurrences: UpdateFlaggedPathOccurrence[];
  reason?: string;
}

export interface UpdateFlaggedPathOccurrence {
  entityId: string;
  allowDiscovery: boolean;
}
